import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Package, Tag } from "lucide-react";
import { toast } from "sonner";
import MediaUploader, { type ImageMeta } from "@/components/admin/MediaUploader";
import BannersManager from "@/components/admin/BannersManager";
import { Search, Image as ImageIcon, Rss, Link2 } from "lucide-react";

interface Product {
  id: string;
  name: string;
  description: string | null;
  price: number;
  cost_price: number;
  stock: number;
  iva_rate: number;
  image_url: string | null;
  is_active: boolean;
  is_promotional: boolean;
  promotional_price: number;
  sku: string | null;
  category: string | null;
  images: string[] | null;
  videos: string[] | null;
  seo_title?: string | null;
  seo_description?: string | null;
  brand?: string | null;
  gtin?: string | null;
  mpn?: string | null;
  google_product_category?: string | null;
  condition?: string | null;
  image_metadata?: ImageMeta | null;
}

const FEED_URL = "https://jgudkyrxbdhpfewdaikz.supabase.co/functions/v1/product-feed";
const SITE = "https://mente-sana-space.lovable.app";

const emptyProduct = {
  name: "",
  description: "",
  price: 0,
  cost_price: 0,
  stock: 0,
  iva_rate: 23,
  image_url: "",
  is_active: true,
  is_promotional: false,
  promotional_price: 0,
  sku: "",
  category: "",
  images: [] as string[],
  videos: [] as string[],
  seo_title: "",
  seo_description: "",
  brand: "Mente Sã",
  gtin: "",
  mpn: "",
  google_product_category: "",
  condition: "new",
  image_metadata: {} as ImageMeta,
};

const copy = async (text: string, msg: string) => {
  try { await navigator.clipboard.writeText(text); toast.success(msg); } catch { toast.error("Não foi possível copiar"); }
};

const ProductsAdmin = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [form, setForm] = useState(emptyProduct);
  const [bannersOpen, setBannersOpen] = useState(false);
  const [query, setQuery] = useState("");

  const fetchProducts = async () => {
    const { data } = await supabase
      .from("products")
      .select("*")
      .order("created_at", { ascending: false });
    setProducts((data as unknown as Product[]) || []);
    setLoading(false);
  };

  useEffect(() => { fetchProducts(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyProduct);
    setDialogOpen(true);
  };

  const openEdit = (p: Product) => {
    setEditing(p);
    setForm({
      name: p.name,
      description: p.description || "",
      price: p.price,
      cost_price: p.cost_price,
      stock: p.stock,
      iva_rate: p.iva_rate,
      image_url: p.image_url || "",
      is_active: p.is_active,
      is_promotional: p.is_promotional,
      promotional_price: p.promotional_price,
      sku: p.sku || "",
      category: p.category || "",
      images: (p.images && p.images.length ? p.images : p.image_url ? [p.image_url] : []) as string[],
      videos: (p.videos || []) as string[],
      seo_title: p.seo_title || "",
      seo_description: p.seo_description || "",
      brand: p.brand || "",
      gtin: p.gtin || "",
      mpn: p.mpn || "",
      google_product_category: p.google_product_category || "",
      condition: p.condition || "new",
      image_metadata: (p.image_metadata || {}) as ImageMeta,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Nome é obrigatório"); return; }

    const payload = {
      name: form.name,
      description: form.description || null,
      price: Number(form.price),
      cost_price: Number(form.cost_price),
      stock: Number(form.stock),
      iva_rate: Number(form.iva_rate),
      image_url: form.images[0] || form.image_url || null,
      images: form.images,
      videos: form.videos,
      sku: form.sku.trim() || null,
      category: form.category.trim() || null,
      is_active: form.is_active,
      is_promotional: form.is_promotional,
      promotional_price: form.is_promotional ? Number(form.promotional_price) : 0,
      seo_title: form.seo_title.trim() || null,
      seo_description: form.seo_description.trim() || null,
      brand: form.brand.trim() || null,
      gtin: form.gtin.trim() || null,
      mpn: form.mpn.trim() || null,
      google_product_category: form.google_product_category.trim() || null,
      condition: form.condition || "new",
      image_metadata: Object.fromEntries(Object.entries(form.image_metadata).filter(([u]) => form.images.includes(u))),
    };

    if (editing) {
      const { error } = await (supabase as any).from("products").update(payload).eq("id", editing.id);
      if (error) { toast.error("Erro ao atualizar"); return; }
      toast.success("Produto atualizado");
    } else {
      const { error } = await (supabase as any).from("products").insert(payload);
      if (error) { toast.error("Erro ao criar"); return; }
      toast.success("Produto criado");
    }
    setDialogOpen(false);
    fetchProducts();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Eliminar este produto?")) return;
    await supabase.from("products").delete().eq("id", id);
    toast.success("Produto eliminado");
    fetchProducts();
  };

  const discountPercent = (original: number, promo: number) => {
    if (original <= 0) return 0;
    return Math.round(((original - promo) / original) * 100);
  };

  const margin = (sell: number, cost: number) => {
    if (sell <= 0) return 0;
    return Math.round(((sell - cost) / sell) * 100);
  };

  const q = query.trim().toLowerCase();
  const filtered = products.filter((p) => !q || [p.name, p.sku, p.category].some((v) => (v || "").toLowerCase().includes(q)));
  const categories = [...new Set(products.map((p) => p.category).filter(Boolean))] as string[];

  if (loading) return <p className="text-muted-foreground">A carregar...</p>;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Produtos & Stock</h1>
          <p className="text-sm text-muted-foreground">Gerir catálogo de produtos da loja virtual</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setBannersOpen(true)} className="gap-2">
            <ImageIcon size={16} /> Banners da Loja
          </Button>
          <Button variant="outline" onClick={() => copy(FEED_URL, "Link do feed XML copiado. Use-o no Google Merchant Center ou no Meta.")} className="gap-2">
            <Rss size={16} /> Copiar feed XML
          </Button>
          <Button onClick={openNew} className="gap-2">
            <Plus size={16} /> Novo Produto
          </Button>
        </div>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" placeholder="Pesquisar por nome, SKU ou categoria" value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>

      {products.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Package size={48} className="mx-auto mb-4 opacity-40" />
          <p>Nenhum produto cadastrado.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filtered.map((p) => (
            <div key={p.id} className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border">
              {p.image_url ? (
                <img src={p.image_url} alt={p.name} className="w-16 h-16 rounded-lg object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center">
                  <Package size={24} className="text-muted-foreground" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-foreground truncate">{p.name}</h3>
                  {p.is_promotional && (
                    <Badge variant="destructive" className="text-[10px] gap-1">
                      <Tag size={10} /> Promocional
                    </Badge>
                  )}
                  {!p.is_active && <Badge variant="secondary" className="text-[10px]">Inativo</Badge>}
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  {p.sku && <span>SKU: {p.sku}</span>}
                  {p.category && <span>{p.category}</span>}
                  <span>Custo: €{p.cost_price.toFixed(2)}</span>
                  {p.is_promotional ? (
                    <>
                      <span className="line-through text-destructive">€{p.price.toFixed(2)}</span>
                      <span className="text-green-600 font-bold">€{p.promotional_price.toFixed(2)}</span>
                      <span className="text-destructive font-semibold">-{discountPercent(p.price, p.promotional_price)}%</span>
                    </>
                  ) : (
                    <span>Venda: €{p.price.toFixed(2)}</span>
                  )}
                  <span>Margem: {margin(p.is_promotional ? p.promotional_price : p.price, p.cost_price)}%</span>
                  <span>Stock: {p.stock}</span>
                  <span>IVA: {p.iva_rate}%</span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="icon" onClick={() => openEdit(p)}>
                  <Pencil size={14} />
                </Button>
                <Button variant="outline" size="icon" onClick={() => handleDelete(p.id)}>
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Produto" : "Novo Produto"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>SKU / Código</Label>
                <Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} placeholder="MS/01" />
              </div>
              <div>
                <Label>Categoria</Label>
                <Input list="product-categories" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Ex.: Difusores" />
                <datalist id="product-categories">{categories.map((c) => <option key={c} value={c} />)}</datalist>
              </div>
            </div>
            <div>
              <Label>Fotos (até 6). Clique numa foto para a tornar principal.</Label>
              <MediaUploader kind="image" max={6} value={form.images} onChange={(images) => setForm((f) => ({ ...f, images }))}
                meta={form.image_metadata} onMetaChange={(image_metadata) => setForm((f) => ({ ...f, image_metadata }))} />
            </div>
            <div>
              <Label>Vídeos (até 5)</Label>
              <MediaUploader kind="video" max={5} value={form.videos} onChange={(videos) => setForm({ ...form, videos })} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Preço de Custo (€)</Label>
                <Input type="number" min={0} step={0.01} value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: parseFloat(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>Preço de Venda (€)</Label>
                <Input type="number" min={0} step={0.01} value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Stock</Label>
                <Input type="number" min={0} value={form.stock} onChange={(e) => setForm({ ...form, stock: parseInt(e.target.value) || 0 })} />
              </div>
              <div>
                <Label>Taxa IVA (%)</Label>
                <Input type="number" min={0} max={100} value={form.iva_rate} onChange={(e) => setForm({ ...form, iva_rate: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>

            {form.price > 0 && form.cost_price > 0 && (
              <p className="text-xs text-muted-foreground">
                Margem de lucro: <strong className="text-foreground">{margin(form.price, form.cost_price)}%</strong>
              </p>
            )}

            <div className="flex items-center gap-3 pt-2">
              <Switch checked={form.is_promotional} onCheckedChange={(v) => setForm({ ...form, is_promotional: v })} />
              <Label>Produto em Promoção</Label>
            </div>

            {form.is_promotional && (
              <div>
                <Label>Preço Promocional (€)</Label>
                <Input type="number" min={0} step={0.01} value={form.promotional_price} onChange={(e) => setForm({ ...form, promotional_price: parseFloat(e.target.value) || 0 })} />
                {form.price > 0 && form.promotional_price > 0 && (
                  <p className="text-xs mt-1 text-muted-foreground">
                    Desconto: <strong className="text-destructive">-{discountPercent(form.price, form.promotional_price)}%</strong>
                  </p>
                )}
              </div>
            )}

            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              <Label>Produto Ativo (visível na loja)</Label>
            </div>

            <div className="space-y-3 rounded-xl border border-border p-4">
              <p className="text-sm font-semibold text-foreground">Google e catálogos (SEO)</p>
              <div>
                <Label>Título para o Google ({form.seo_title.length}/150)</Label>
                <Input maxLength={150} value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} placeholder={form.name || "Ex.: Óleo de massagem relaxante 100ml"} />
              </div>
              <div>
                <Label>Descrição para o Google ({form.seo_description.length}/5000)</Label>
                <Textarea maxLength={5000} rows={3} value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Marca</Label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></div>
                <div>
                  <Label>Estado</Label>
                  <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })}>
                    <option value="new">Novo</option><option value="used">Usado</option><option value="refurbished">Recondicionado</option>
                  </select>
                </div>
                <div><Label>Código de barras (GTIN/EAN)</Label><Input value={form.gtin} onChange={(e) => setForm({ ...form, gtin: e.target.value })} /></div>
                <div><Label>Referência do fabricante (MPN)</Label><Input value={form.mpn} onChange={(e) => setForm({ ...form, mpn: e.target.value })} /></div>
              </div>
              <div>
                <Label>Categoria Google (opcional)</Label>
                <Input value={form.google_product_category} onChange={(e) => setForm({ ...form, google_product_category: e.target.value })} placeholder="Ex.: Saúde e beleza > Cuidados pessoais" />
              </div>
              {editing && (
                <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => copy(`${SITE}/loja?produto=${editing.id}`, "Link do produto copiado")}>
                  <Link2 size={14} /> Copiar link do produto
                </Button>
              )}
            </div>

            <Button onClick={handleSave} className="w-full">{editing ? "Guardar Alterações" : "Criar Produto"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={bannersOpen} onOpenChange={setBannersOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Banners de Promoção da Loja</DialogTitle></DialogHeader>
          <BannersManager />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductsAdmin;
